import {AuthService} from './../core/auth.service';
import {ArchiveDemandResponse, ArchiveEtollFrame, ArchiveRequestResponse, ArchiveResponse,} from './../models/api/archive.model';
import {Router} from '@angular/router';
import {Injectable} from '@angular/core';
import {BehaviorSubject, iif, MonoTypeOperatorFunction, Observable, of, Subject, Subscription, throwError,} from 'rxjs';
import {ArchiveData, ArchivePosition} from '../models/api/archive.model';
import {environment} from '../../environments/environment';
import {MessageService} from 'primeng/api';
import {HttpClient} from '@angular/common/http';
import {SidenavService} from '../core/sidenav.service';
import * as moment from 'moment';
import {AppStore} from '../core/state/app-store';
import {VehicleStatuses} from '../models/vehicle-statuses.model';
import {Options, parseString} from 'xml2js';
import {InitialVehiclesPositionDTO} from './../models/api/initial-vehicles-positions.model';
import {concatMap, delay, finalize, map, retryWhen, switchMap,} from 'rxjs/operators';
import {version} from '../../../package.json';

export interface DateOptions {
  timeFrom: string;
  timeTo: string;
  dateFrom: Date;
  dateTo: Date;
}

export interface TimeStamp {
  from: number;
  to: number;
}

interface MenuState {
  tab: string;
  calendarExpanded: boolean;
  dateOptions: DateOptions;
}

@Injectable({
  providedIn: 'root',
})
export class ArchiveService {
  public colors = [
    { value: '#000000', used: false },
    { value: '#23799e', used: false },
    { value: '#3f630d', used: false },
    { value: '#003761', used: false },
    { value: '#9185be', used: false },
    { value: '#e4b501', used: false },
    { value: '#8ec89a', used: false },
    { value: '#a4917e', used: false },
    { value: '#898989', used: false },
    { value: '#f3997b', used: false },
    { value: '#cfe09b', used: false },
    { value: '#488ccb', used: false },
    { value: '#bb3333', used: false },
    { value: '#33bb33', used: false },
    { value: '#3333bb', used: false },
    { value: '#bb33bb', used: false },
    { value: '#bbbb33', used: false },
    { value: '#33bbbb', used: false },
    { value: '#bbbbbb', used: false },
  ];
  private thermometers = [
    {
      temperatureColumn: 'EXT_DST1',
      temperature: 'bluetoothTemp1',
      humidityColumn: 'EXT_DSTB16_HUMIDITY',
      humidity: 'bluetoothHumidity1',
      powerColumn: 'EXT_DSTB16_POWER_LEVEL',
    },
    {
      temperatureColumn: 'EXT_DST2',
      temperature: 'bluetoothTemp2',
      humidityColumn: 'EXT_DSTB17_HUMIDITY',
      humidity: 'bluetoothHumidity2',
      powerColumn: 'EXT_DSTB17_POWER_LEVEL',
    },
    {
      temperatureColumn: 'EXT_DST3',
      temperature: 'bluetoothTemp3',
      humidityColumn: 'EXT_DSTB18_HUMIDITY',
      humidity: 'bluetoothHumidity3',
      powerColumn: 'EXT_DSTB18_POWER_LEVEL',
    },
    {
      temperatureColumn: 'EXT_DST4',
      temperature: 'bluetoothTemp4',
      humidityColumn: 'EXT_DSTB19_HUMIDITY',
      humidity: 'bluetoothHumidity4',
      powerColumn: 'EXT_DSTB19_POWER_LEVEL',
    },
  ];

  focusFrame$: Observable<ArchivePosition>;
  private _focusFrame$: BehaviorSubject<ArchivePosition>;
  archiveData$: Observable<ArchiveData[]>;
  private _archiveData$: BehaviorSubject<ArchiveData[]>;
  archiveFocus$: Observable<number>;
  private readonly _archiveFocus$: Subject<number>;
  private _selectedVehicle: string;
  private _dateRange: number[] = [];
  private readonly _menuState: MenuState;
  private readonly urlArchive = environment.baseUrl + 'archive';
  private readonly urlArchiveEtoll =
    'https://dslocate3.datasystem.pl/archive/v1/devices/archive';
  constructor(
    private messageService: MessageService,
    private sidenavService: SidenavService,
    private http: HttpClient,
    private appStore: AppStore,
    private router: Router,
    private authService: AuthService
  ) {
    // Observable for selection on archive list <-> archive map
    this._focusFrame$ = new BehaviorSubject(null);
    this.focusFrame$ = this._focusFrame$.asObservable();
    // Observable for watching archive data change.
    this._archiveData$ = new BehaviorSubject([]);
    this.archiveData$ = this._archiveData$.asObservable();
    // Observable for tracking archive focus.
    this._archiveFocus$ = new Subject();
    this.archiveFocus$ = this._archiveFocus$.asObservable();
    // Saving menu state after options component is destroyed
    this._menuState = {
      tab: 'options',
      calendarExpanded: false,
      dateOptions: {
        timeFrom: '00:00',
        timeTo: '23:59',
        dateFrom: new Date(),
        dateTo: new Date(),
      },
    };
  }

  calculateDateRangeForPreset(start: number, end: number) {
    this._dateRange = [start, end];
  }

  calculateDateRangeForCustom(start: number, end: number) {
    this._dateRange = [start, end];
  }

  public calculateTimestamp(
    option: string,
    dateOptions: DateOptions
  ): TimeStamp {
    let today = new Date();
    let timestamp = {
      from: 0,
      to: 0,
    };
    switch (option) {
      case 'custom':
        {
          let timeFrom = dateOptions.timeFrom.split(':');
          let timeTo = dateOptions.timeTo.split(':');
          timestamp.from =
            dateOptions.dateFrom.getTime() +
            (Number(timeFrom[0]) * 3600 + Number(timeFrom[1]) * 60) * 1000;
          timestamp.to =
            dateOptions.dateTo.getTime() +
            (Number(timeTo[0]) * 3600 + Number(timeTo[1]) * 60) * 1000;
        }
        break;
      case 'yesterday':
        {
          timestamp.from = moment()
            .subtract(1, 'days')
            .startOf('day')
            .valueOf();
          timestamp.to = moment().subtract(1, 'days').endOf('day').valueOf();
        }
        break;
      case 'today':
        {
          timestamp.from = moment(today).startOf('day').valueOf();
          timestamp.to = today.getTime();
        }
        break;
      case 'last-7-days':
        // Ex. Today is 21.06 (Tuesday). Date from should be 15.06 0:00. Date to should be now()
        {
          timestamp.from = moment(today)
            .subtract(6, 'day')
            .startOf('day')
            .valueOf();
          timestamp.to = today.getTime();
        }
        break;
      case 'last-week':
        {
          timestamp.from = moment(today)
            .subtract(1, 'week')
            .startOf('isoWeek')
            .valueOf();
          timestamp.to = moment(today)
            .subtract(1, 'week')
            .endOf('isoWeek')
            .valueOf();
        }
        break;
      case 'last-month':
        {
          timestamp.from = moment(today)
            .subtract(1, 'month')
            .startOf('month')
            .valueOf();
          timestamp.to = moment(today)
            .subtract(1, 'month')
            .endOf('month')
            .valueOf();
        }
        break;
    }
    return timestamp;
  }
  toStringOrUndefined = (value) => {
    return value !== undefined ? String(value) : value;
  };

  mapBluetoothThermometers = (vehicle) => {
    const extColumnList = [];
    for (const thermometer of this.thermometers) {
      if (vehicle[thermometer.temperature]) {
        extColumnList.push({
          name: thermometer.temperatureColumn,
          value: vehicle[thermometer.temperature],
        });
        extColumnList.push({
          name: thermometer.powerColumn,
          value: 100, //Temporary solution, backend does't have this data yet
        });
      }
      if (vehicle[thermometer.humidity]) {
        extColumnList.push({
          name: thermometer.humidityColumn,
          value: vehicle[thermometer.humidity],
        });
      }
    }
    return extColumnList;
  };

  mapExtColumns = (vehicle) => {
    const extColumnList = [];
    if (vehicle.ext)
      for (const key of Object.keys(vehicle.ext)) {
        extColumnList.push({ name: key, value: vehicle.ext[key] });
      }
    return extColumnList;
  };

  public downloadArchive(selectedVehicle: any) {
    this.archiveData.push(new ArchiveData());
    let color = this.getOrGenerateColor();
    this.archiveData.slice(-1)[0].color = color.value;
    const vehicleType = this.findVehicleTypeById(selectedVehicle.value);

    if (this.authService.isDSLocateUser()) {
      this.processEtollArchiveDownload(selectedVehicle, vehicleType);
    } else this.processOldArchiveDownload(selectedVehicle, vehicleType);
  }

  private processEtollArchiveDownload(
    selectedVehicle: any,
    vehicleType: string
  ) {
    this.http
      .get(
        this.urlArchiveEtoll +
          `?deviceId=${selectedVehicle.value}&from=${new Date(
            this.dateRange[0]
          ).toISOString()}&optimize=false&to=${new Date(
            this.dateRange[1]
          ).toISOString()}`,
        { headers: this.authService.getJwtAuthHeaders() }
      )
      .subscribe((frames: ArchiveEtollFrame[]) => {
        let archive: ArchivePosition[] = frames.map((frame) => {
          return this.mapEtollFrame(
            frame,
            selectedVehicle.value,
            vehicleType,
            selectedVehicle
          );
        });
        this.updateArchiveState(archive, selectedVehicle.label);
        this.publishArchive(false);
        if (/archive/.test(this.router.url)) {
          this.router.navigate(['/archive/list']);
        }
        this.sidenavService.closeSideNavIfSmallWidth();
      });
  }

  private processOldArchiveDownload(selectedVehicle: any, vehicleType: string) {
    this.http
      .get(
        this.urlArchive +
          `?vehicleId=${selectedVehicle.value}&dateFrom=${new Date(
            this.dateRange[0]
          ).toISOString()}&dateTo=${new Date(this.dateRange[1]).toISOString()}`,
        { headers: this.authService.getBasicAuthHeaders() }
      )
      .subscribe((archiveResponse: ArchiveResponse) => {
        let uncompressedArchive = archiveResponse.entries
          .reduce((prev: any[], curr) => {
            return this.uncompressOldArchive(prev, curr);
          }, [])
          .map((vehicle) => {
            return this.mapOldFrame(
              archiveResponse,
              vehicleType,
              selectedVehicle,
              vehicle
            );
          });
        this.updateArchiveState(uncompressedArchive, selectedVehicle.label);
        this.publishArchive(false);
        if (/archive/.test(this.router.url)) {
          this.router.navigate(['/archive/list']);
        }
        this.sidenavService.closeSideNavIfSmallWidth();
      });
  }

  private uncompressOldArchive(prev: any[], curr: { [key: string]: any }) {
    let last = {};
    let lastExtColumns = {};
    if (prev.length > 0) {
      last = { ...prev[prev.length - 1] };
      if (prev[prev.length - 1].ext)
        lastExtColumns = { ...prev[prev.length - 1].ext };
    }
    prev.push({
      ...last,
      ...curr,
      ext: { ...lastExtColumns, ...curr.ext },
    });
    return prev;
  }

  private mapEtollFrame(
    frame: ArchiveEtollFrame,
    deviceId: string,
    vehicleType: string,
    selectedVehicle: any
  ): ArchivePosition {
    return {
      vehicle: deviceId,
      type: vehicleType,
      name: selectedVehicle.label,
      lat: this.toStringOrUndefined(frame.latitude),
      lon: this.toStringOrUndefined(frame.longitude),
      speed: frame.ignition ? this.toStringOrUndefined(frame.speed) : '255',
      dist: this.toStringOrUndefined(frame.distance),
      hdg: this.toStringOrUndefined(frame.direction),
      fuel: this.toStringOrUndefined(undefined),
      sat: this.toStringOrUndefined(frame.satellites),
      dallas: this.toStringOrUndefined(undefined),
      altdallas: this.toStringOrUndefined(undefined),
      time: this.toStringOrUndefined(frame.time),
      extcolumns: {
        ext: this.mapExtColumns(frame).concat(
          this.mapBluetoothThermometers(frame)
        ),
      },
      battery: undefined,
      suply: frame.supply,
      pin: undefined,
      tail: undefined,
      vstats: { sparam: [] },
      statusicons: {
        icon: [
          {
            token:
              frame.supply > 1
                ? VehicleStatuses.OK
                : VehicleStatuses.POWER_WARNING,
            priority: '1',
          },
        ],
      },
    } as ArchivePosition;
  }

  private mapOldFrame(
    archiveResponse: ArchiveResponse,
    vehicleType: string,
    selectedVehicle: any,
    vehicle: any
  ) {
    return {
      vehicle: archiveResponse.vehicleId,
      type: vehicleType,
      name: selectedVehicle.label,
      lat: this.toStringOrUndefined(vehicle.lat),
      lon: this.toStringOrUndefined(vehicle.lon),
      speed: vehicle.engineRunning
        ? this.toStringOrUndefined(vehicle.speed)
        : '255',
      dist: this.toStringOrUndefined(vehicle.distance),
      hdg: this.toStringOrUndefined(vehicle.heading),
      fuel: this.toStringOrUndefined(vehicle.fuel),
      sat: this.toStringOrUndefined(vehicle.satellities),
      dallas: this.toStringOrUndefined(vehicle.dallasId),
      altdallas: this.toStringOrUndefined(vehicle.altDallasId),
      time: this.toStringOrUndefined(vehicle.timestamp),
      extcolumns: { ext: this.mapExtColumns(vehicle) },
      battery: vehicle.battery,
      suply: vehicle.suply,
    };
  }

  private getOrGenerateColor() {
    let color = this.colors.find((color) => !color.used);
    if (color) {
      color.used = true;
    } else {
      color = {
        value: '#' + Math.floor(Math.random() * 16777215).toString(16),
        used: true,
      };
      this.colors.push(color);
    }
    return color;
  }

  showMessage(error: string, severity?: string) {
    this.messageService.add({
      severity: severity ? severity : 'warn',
      detail: error,
      key: 'appMessage',
    });
  }

  updateArchiveState(positions: ArchivePosition[], vehicleName: string): void {
    const archive = this.archiveData[this.archiveData.length - 1];
    archive.vehicleName = vehicleName;
    archive.positionsWithStops = archive.positionsWithStops.concat(positions);
    archive.positions = this.filterStops(archive.positionsWithStops);
    this.archiveData[this.archiveData.length - 1] = archive;
    this._archiveData$.next(this.archiveData);
    this._archiveFocus$.next(this.archiveData.length - 1);
  }

  publishArchive(isArchiveFromCSV: boolean) {
    const archive = this.archiveData.slice(-1)[0];
    archive.dateRange = this._dateRange;
    archive.isArchiveFromCSV = isArchiveFromCSV;
    archive.loading = false;
    archive.positionsWithoutStops = this.filterStops(
      archive.positionsWithStops
    );
    if (isArchiveFromCSV) {
      let color = this.getOrGenerateColor();
      archive.color = color.value;
    }
    archive.ignoreStops = this.checkIfIgnoreStops(archive.positionsWithStops);
    this.choosePositionsToShow(archive);
    this.archiveData.map((data, idx) => {
      if (idx === this.archiveData.length - 1) {
        return;
      }
      data.expanded = false;
    });
    this.setArchiveData(this.archiveData);
  }

  filterStops(positions: ArchivePosition[]) {
    return positions
      .filter(
        (pos, idx, array) =>
          pos.speed !== '255' ||
          (array[idx - 1] && array[idx - 1].speed !== '255') ||
          (array[idx + 1] && array[idx + 1].speed !== '255') ||
          idx === array.length - 1
      )
      .filter(
        (pos, idx, array) =>
          pos.speed !== '255' ||
          (array[idx - 1] && array[idx - 1].speed !== '255') ||
          idx === array.length - 1 ||
          !idx
      )
      .filter(
        (pos, idx, array) =>
          !(
            pos.speed === '255' &&
            array[idx + 1] &&
            array[idx + 1].speed === '255'
          )
      );
  }

  findVehicleTypeById(vehicleId): string {
    const vehicle = this.appStore
      .getState()
      .vehicles.find((vehicle) => vehicle.id === parseInt(vehicleId));
    return vehicle ? vehicle.type : 'osobowy';
  }

  choosePositionsToShow(archiveData: ArchiveData) {
    if (archiveData.oneDataPerMin) {
      if (archiveData.ignoreStops) {
        let time = Number(archiveData.positionsWithoutStops[0].time) - 60;
        archiveData.positions = archiveData.positionsWithoutStops.filter(
          (position) => {
            if (Number(position.time) - 58 > time) {
              time = Number(position.time);
              return true;
            } else {
              return false;
            }
          }
        );
      } else {
        let time = Number(archiveData.positionsWithStops[0].time) - 60;
        archiveData.positions = archiveData.positionsWithStops.filter(
          (position) => {
            if (Number(position.time) - 58 > time) {
              time = Number(position.time);
              return true;
            } else {
              return false;
            }
          }
        );
      }
    } else {
      if (archiveData.ignoreStops) {
        archiveData.positions = archiveData.positionsWithoutStops;
      } else {
        archiveData.positions = archiveData.positionsWithStops;
      }
    }
  }

  checkIfIgnoreStops(positions: ArchivePosition[]) {
    if (positions && positions.length) {
      const diff =
        moment(parseInt(positions.slice(-1)[0].time)).diff(
          moment(parseInt(positions[0].time)),
          'days'
        ) + 1;
      return (this.archiveData.slice(-1)[0].ignoreStops =
        positions.length / diff > 10);
    }
    return true;
  }

  isArchiveLoading() {
    return this.archiveData.find((data) => data.loading) != null;
  }

  get archiveData(): ArchiveData[] {
    return this._archiveData$.getValue();
  }

  setArchiveData(value: ArchiveData[]) {
    this._archiveData$.next(value);
  }

  get archiveFocus(): Subject<number> {
    return this._archiveFocus$;
  }

  setArchiveFocus(value: number) {
    this._archiveFocus$.next(value);
  }

  get focusFrame(): ArchivePosition {
    return this._focusFrame$.getValue();
  }

  setFocusFrame(next: ArchivePosition): void {
    this._focusFrame$.next(next);
  }

  setFocusFrameByTimestamp(time: number): void {
    this.archiveData.forEach((data) => {
      const nextFrame = data.positions.find(
        (position) => position.time === time.toString()
      );
      if (nextFrame) {
        this.setFocusFrame(nextFrame);
        return;
      }
    });
  }

  setSelectedVehicle(value: any) {
    this._selectedVehicle = value;
  }

  get selectedVehicle(): any {
    return this._selectedVehicle;
  }

  setDateRange(value: number[]) {
    this._dateRange = value;
  }

  get dateRange(): number[] {
    return this._dateRange;
  }

  setMenuState(key: string, value: any) {
    this._menuState[key] = value;
  }

  get menuState(): MenuState {
    return this._menuState;
  }

  /*
    Old archive. Temporary solution for technical department
  */

  private readonly urlInitialVehiclesPosition = environment.baseUrl + 'login';

  xmlParserOptions: Options = { explicitArray: false, mergeAttrs: true };

  private readonly urlRequestArchive = environment.baseUrl + 'request/archive';
  private readonly urlDemandArchive = environment.baseUrl + 'demand/archive';

  downloadPart$: Observable<number>;
  private _downloadPart$: BehaviorSubject<number>;
  private _downloader: Subscription;
  MS_PER_DAY = 24 * 3600 * 1000;
  get downloadPart(): number {
    return this._downloadPart$.getValue();
  }

  setDownloadPart(next: number): void {
    this._downloadPart$.next(next);
  }

  private xmlParser<T>(xml: string): T {
    let convertedResponse: T;
    parseString(xml, this.xmlParserOptions, (err, data) => {
      if (err) {
        throw err;
      }
      convertedResponse = data;
    });
    return convertedResponse;
  }

  calculateDateRangeForPresetOld(start: number, end: number) {
    const daysBetween: number =
      moment(end).endOf('day').diff(moment(start).startOf('day'), 'day') + 1;
    this._dateRange = Array.from(
      new Array(daysBetween + 1),
      (v, i) => new Date(start + i * this.MS_PER_DAY).getTime() / 1000
    );
  }

  calculateDateRangeForCustomOld(start: number, end: number) {
    let dateRange: number[];
    if (end - start <= this.MS_PER_DAY) {
      this._dateRange = [start / 1000, end / 1000];
    }
    let dateToBegin = moment(start).startOf('day').add(1, 'day');
    const daysBetween: number = Math.ceil(
      (end - dateToBegin.valueOf()) / this.MS_PER_DAY
    );
    dateRange = Array.from(
      new Array(daysBetween),
      (v, i) =>
        new Date(dateToBegin.valueOf() + i * this.MS_PER_DAY).getTime() / 1000
    );
    this._dateRange = [start / 1000, ...dateRange, end / 1000];
  }

  getConnctionToken(): Observable<string> {
    const { login, password } = this.authService.getUser();
    const requestBody = `<login user="${login}" pass="${password}" />`;
    return this.http
      .post(this.urlInitialVehiclesPosition, requestBody, {
        responseType: 'text',
        headers: {
          'App-Version': version,
        },
      })
      .pipe(
        map((res: string) =>
          this.xmlParser<InitialVehiclesPositionDTO>(
            res.replace(/amp;/g, '').replace(/&/g, '&amp;')
          )
        ),
        map((data) => {
          return data.vehicles_data.connection_token;
        })
      );
  }

  public downloadFromOldArchive(selectedVehicle: any): Observable<void> {
    return this.getConnctionToken().pipe(
      map((token) => {
        let loadArchivePart: Subject<boolean> = new Subject();
        let archiveId: string = '';

        // Observable for options component to show actual progress of downloading
        this._downloadPart$ = new BehaviorSubject(0);
        this.downloadPart$ = this._downloadPart$.asObservable();

        this._downloadPart$.next(0);
        this.archiveData.push(new ArchiveData());
        let color = this.getOrGenerateColor();
        this.archiveData.slice(-1)[0].color = color.value;
        const vehicleType = this.findVehicleTypeById(selectedVehicle.value);
        this._downloader = loadArchivePart
          .pipe(
            finalize(() => {
              // If all framers are downloaded finish
              if (!this.archiveData.slice(-1)[0].positions.length) {
                this.archiveData.pop();
              } else {
                this.publishArchive(false);
              }
              if (/archive/.test(this.router.url)) {
                this.router.navigate(['/archive/list']);
              }
              this.sidenavService.closeSideNavIfSmallWidth();
            }),
            switchMap((nextPart: boolean) => {
              if (nextPart && this.isArchiveLoading()) {
                return this.requestArchive(
                  token,
                  selectedVehicle.value,
                  this._dateRange[this._downloadPart$.value],
                  this._dateRange[this._downloadPart$.value + 1]
                ).pipe(
                  switchMap((resp: ArchiveRequestResponse) => {
                    archiveId = resp.requestarchive.archive_id;
                    return this.demandArchive(
                      token,
                      resp.requestarchive.archive_id
                    );
                  }),
                  this.retryAfterError(5, 2000)
                );
              } else {
                if (!this.isArchiveLoading()) {
                  this._downloader.unsubscribe();
                  return;
                }
                return this.demandArchive(token, archiveId).pipe(
                  delay(2000),
                  this.retryAfterError(5, 2000)
                );
              }
            })
          )
          .subscribe(
            (resp: ArchiveDemandResponse) => {
              if (!this.isArchiveLoading()) {
                this._downloader.unsubscribe();
                return;
              }
              if (resp.disconnect) {
                loadArchivePart.next(false);
                return;
              }
              if (resp.vehicles_data.archive.position) {
                Array.from(resp.vehicles_data.archive.position).map(
                  (position) => {
                    position.type = vehicleType;
                    position.name = selectedVehicle.label;
                    return position;
                  }
                );
                // Update archive state in store.
                this.updateArchiveStateOld(resp, selectedVehicle.label);
              }
              // next part
              this.setDownloadPart(this._downloadPart$.value + 1);
              if (
                this._downloadPart$.value < this._dateRange.length - 1 &&
                this.isArchiveLoading()
              ) {
                loadArchivePart.next(true);
              } else {
                if (!this.archiveData.slice(-1)[0].positions.length) {
                  this.showMessage('archive.messages.no - results');
                }
                this._downloader.unsubscribe();
              }
            },
            () => {
              this.showMessage('archive.errors.general-error', 'error');
              this._downloader.unsubscribe();
              this.setDownloadPart(this._dateRange.length - 1);
            }
          );
        loadArchivePart.next(true);
      })
    );
  }
  requestArchive(
    token: string,
    selectedVehicle: number,
    timeFrom: number,
    timeTo: number
  ): Observable<ArchiveRequestResponse> {
    const body = `<request_archive connection_token="${token}" request_id="1234" vehicle="${selectedVehicle}" from="${timeFrom}" to="${timeTo}"/>`;
    return this.http
      .post(this.urlRequestArchive, body, {
        responseType: 'text',
        headers: {
          'App-Version': version,
        },
      })
      .pipe(
        map((res: string) =>
          this.xmlParser<ArchiveRequestResponse>(
            res.replace(/amp;/g, '').replace(/&/g, '&amp;')
          )
        )
      );
  }

  demandArchive(
    token: string,
    archiveId: string
  ): Observable<ArchiveDemandResponse> {
    const body = `<demand_archive connection_token="${token}" archive_id="${archiveId}"/>`;
    return this.http
      .post(this.urlDemandArchive, body, {
        responseType: 'text',
        headers: {
          'App-Version': version,
        },
      })
      .pipe(
        map((res: string) =>
          this.xmlParser<ArchiveDemandResponse>(
            res.replace(/amp;/g, '').replace(/&/g, '&amp;')
          )
        )
      );
  }

  retryAfterError(
    retryLimit: number,
    delayTime: number
  ): MonoTypeOperatorFunction<any> {
    return retryWhen((errors) =>
      errors.pipe(
        concatMap((error, attempt) =>
          iif(
            () => attempt > retryLimit,
            throwError(error),
            of(error).pipe(delay(delayTime))
          )
        )
      )
    );
  }

  updateArchiveStateOld(
    update: ArchiveDemandResponse,
    vehicleName: string
  ): void {
    const archive = this.archiveData[this.archiveData.length - 1];
    archive.vehicleName = vehicleName;
    archive.positionsWithStops = archive.positionsWithStops.concat(
      update.vehicles_data.archive.position
    );
    archive.positions = this.filterStops(archive.positionsWithStops);
    this.archiveData[this.archiveData.length - 1] = archive;
    this._archiveData$.next(this.archiveData);
    this._archiveFocus$.next(this.archiveData.length - 1);
  }
}
